import React from 'react';
import asyncSeries from 'async/series';
import EventEmitter from 'events';

import DeviceHashStore from '../stores/device_hash_store';
import MediaStore from '../stores/media_store';

import HashCache from './hash_cache';

import { AsyncIterator } from '../tools/async_iterator';
import S3Helper from '../tools/s3_helper';
import util from '../tools/util';

export default {
  getUploadList,
  getSummary,
  useUploadList,
  useSummary,
  ingest,
};

const ERROR_LIMIT = 5;
const RETRY_INTERVAL = 10 * 1000;
const UPLOAD_LIMIT = 10;

let g_itemId = 1;
let g_list: any[] = [];
let g_uploadList: any[] = [];
let g_summary: any = {};

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useUploadList() {
  return React.useSyncExternalStore(_subscribe, getUploadList);
}
export function useSummary() {
  return React.useSyncExternalStore(_subscribe, getSummary);
}
function getUploadList() {
  return g_uploadList;
}
function getSummary() {
  return g_summary;
}
function _recalcList() {
  const new_list = g_list.map((item) => ({
    name: item.name,
    dir: item.dir,
    file_size: item.file_size,
    progress: item.progress,
    status: item.status,
    fatal_err: item.fatal_err,
    last_err: item.last_err,
    skip: item.skip,
    is_done: item.is_done,
  }));
  if (!util.deepEqual(new_list, g_uploadList)) {
    g_uploadList = new_list;
    _emit('upload_list');
  }
}
function _recalcSummary() {
  const summary = {
    file_count: g_list?.length ?? 0,
    total_size: g_list?.reduce?.((m, i) => m + i.file_size, 0) ?? 0,
    success_count: g_list?.reduce?.((m, i) => m + (i.is_done ? 1 : 0), 0) ?? 0,
    success_size:
      g_list?.reduce?.((m, i) => m + (i.is_done ? i.file_size : 0), 0) ?? 0,
    fail_count: g_list?.reduce?.((m, i) => m + (i.fatal_err ? 1 : 0), 0) ?? 0,
    fail_size:
      g_list?.reduce?.((m, i) => m + (i.fatal_err ? i.file_size : 0), 0) ?? 0,
    skip_count: g_list?.reduce?.((m, i) => m + (i.skip ? 1 : 0), 0) ?? 0,
    skip_size:
      g_list?.reduce?.((m, i) => m + (i.skip ? i.file_size : 0), 0) ?? 0,
  };
  if (!util.deepEqual(summary, g_summary)) {
    g_summary = summary;
    _emit('summary');
  }
}
type IngestParams = {
  volume_id: number;
  list: any[];
};
function ingest(params: IngestParams, done) {
  const { volume_id } = params;
  const list = params.list.map((item) => ({
    item_id: g_itemId++,
    volume_id,
    media_path: item.media_path,
    file: item.file,
    name: item.name,
    file_size: item.file_size,
    dir: item.dir,
    status: 'idle',
    upload_tries: 0,
    progress: 0,
    hash: null,
    fatal_err: null,
    last_err: null,
    skip: false,
    last_updated: Date.now(),
    start_upload_time: 0,
    is_done: false,
  }));
  g_list = list;
  _recalcSummary();
  _emit('start');

  const opts = {
    list,
    limit: UPLOAD_LIMIT,
    times: ERROR_LIMIT,
    interval: RETRY_INTERVAL,
    iterator: _uploadFile,
    errorFilter: _errorFilter,
  };
  const iter = new AsyncIterator(opts);
  iter.on('item_final_error', (err, item) => {
    item.fatal_err = err;
    _itemStatus(item, 'error');
    _recalcSummary();
    _emit('item_fatal_error', item, err);
  });
  iter.on('done', (err) => {
    _recalcSummary();
    _emit('done', err);
    done();
  });
  iter.start();
}

function _uploadFile(item, done) {
  const { file, media_path, file_size, volume_id } = item;
  const mtime = file.lastModified;

  let file_hash: string;
  let part_list: any[];
  let media: any;

  item.upload_tries++;
  item.progress = 0;
  item.last_err = null;
  _itemStatus(item, 'start');
  asyncSeries(
    [
      (done) => {
        MediaStore.findMedia({ volume_id, media_path }, (err, found) => {
          if (err) {
            util.errorLog(
              'MediaIngest._uploadFile: find err:',
              err,
              media_path
            );
          } else if (found) {
            media = found;
          }
          done(err);
        });
      },
      (done) => {
        _itemStatus(item, 'hashing');
        HashCache.hashFile(file, (err, result) => {
          if (err) {
            util.errorLog(
              'MediaIngest._uploadFile: hash err:',
              err,
              media_path
            );
          } else {
            file_hash = result.hash;
            part_list = result.part_list;
            item.hash = result.hash;
            if (media && media.file_hash !== file_hash) {
              item.fatal_err = 'hash_mismatch';
              err = 'conflict';
            }
          }
          _itemStatus(item, 'hash_done');
          done(err);
        });
      },
      (done) => {
        if (media) {
          done();
        } else {
          const opts = {
            volume_id,
            media_path,
            file_size,
            file_hash,
            ctime: mtime,
            mtime,
          };
          MediaStore.addMedia(opts, (err, new_media) => {
            if (err) {
              util.errorLog(
                'MediaIngest._uploadFile: add err:',
                err,
                media_path
              );
            }
            media = new_media;
            done(err);
          });
        }
      },
      (done) => {
        const opts = {
          volume_id,
          item,
          file,
          file_hash,
          file_size,
          part_list,
        };
        const func = part_list.length > 1 ? _multiUpload : _singleUpload;
        func(opts, done);
      },
    ],
    (err?: any) => {
      if (err === 'skip') {
        item.skip = true;
        err = null;
      }
      item.last_err = err;
      if (err) {
        item.progress = 0;
        _itemStatus(item, 'waiting_retry');
      } else {
        item.progress = 1;
        item.is_done = true;
        _itemStatus(item, 'done');
      }
      _recalcSummary();
      done(err);
    }
  );
}
function _singleUpload(params, done) {
  const { item, volume_id, file, file_hash, file_size } = params;
  let check_result;
  asyncSeries(
    [
      (done) => {
        const opts = {
          volume_id,
          file_list: [{ file_hash, file_size }],
        };
        DeviceHashStore.checkUploadList(opts, (err, results) => {
          if (err) {
            util.errorLog(
              'MediaIngest._singleUpload: check err:',
              err,
              file_hash
            );
          } else if (results?.length > 0) {
            check_result = results[0];
            if (check_result?.skip_upload) {
              err = 'skip';
            }
          } else {
            err = 'skip';
          }
          done(err);
        });
      },
      (done) => {
        function progress(fraction: number) {
          item.progress = fraction;
          _recalcList();
          _emit('item_progress', item);
        }
        _itemStatus(item, 'uploading');
        const opts = {
          request: check_result.signed_opts,
          file,
          progress,
        };
        S3Helper.sendFile(opts, done);
      },
      (done) => {
        const opts = {
          volume_id,
          s3_bucket_id: check_result.s3_bucket_id,
          file_hash,
          file_size,
        };
        DeviceHashStore.addS3Object(opts, (err) => {
          if (err) {
            util.errorLog('MediaIngest._sendS3: err:', err, file_hash);
          }
          done(err);
        });
      },
    ],
    done
  );
}
function _multiUpload(params, done) {
  const { item, volume_id, file, file_hash, file_size, part_list } = params;

  let check_result: any;
  asyncSeries(
    [
      (done) => {
        const opts = {
          volume_id,
          file_hash,
          file_size,
          part_list,
        };
        DeviceHashStore.createMultipart(opts, (err, results) => {
          if (err) {
            util.errorLog(
              'MediaIngest._multiUpload: check err:',
              err,
              file_hash
            );
          } else if (results?.skip_upload) {
            err = 'skip';
          }
          check_result = results;
          done(err);
        });
      },
      (done) => {
        function progress(fraction: number) {
          item.progress = fraction;
          _recalcList();
          _emit('item_progress', item);
        }
        _itemStatus(item, 'uploading');
        const opts = {
          part_list,
          upload_list: check_result.upload_list,
          file,
          progress,
        };
        S3Helper.sendParts(opts, done);
      },
      (done) => {
        const opts = {
          volume_id,
          s3_bucket_id: check_result.s3_bucket_id,
          upload_id: check_result.upload_id,
          file_hash,
          file_size,
          part_list,
        };
        DeviceHashStore.completeMultipart(opts, (err) => {
          if (err) {
            util.errorLog('MediaIngest._multiUpload: err:', err, file_hash);
          }
          done(err);
        });
      },
    ],
    done
  );
}
function _errorFilter(err, item) {
  let ret = true;
  if (item.fatal_err) {
    util.errorLog('MediaIngest._errorFilter: stop:', err, item.fatal_err, item);
    ret = false;
  }
  return ret;
}
function _itemStatus(item, status) {
  item.status = status;
  item.last_updated = Date.now();
  _recalcList();
  _emit('item_update', item);
}
